import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { SocialSignIn } from '../../components/social-sign-in/social-sign-in';

/**
 * Copy for the `?error=` codes better-auth appends when an OAuth round-trip
 * fails. `account_not_linked` is deliberately hedged: stating outright that an
 * account exists would leak that fact to someone holding an unverified provider
 * email for the address, which is the same enumeration rule the reset flow follows.
 */
const SOCIAL_ERROR_COPY: Record<string, string> = {
  access_denied: 'Sign-in was cancelled.',
  account_not_linked:
    "We couldn't sign you in with that account. If you already have a FlippyBoards login for this email, use your password instead.",
  email_not_found:
    'That account has no email address we can read. Add one in your provider settings and try again.',
};

function socialErrorCopy(code: string | null): string | null {
  if (!code) return null;
  return SOCIAL_ERROR_COPY[code] ?? 'Sign-in failed. Please try again.';
}

/** Only same-origin paths are safe to hand to the OAuth callbackURL. */
function safeRedirectPath(raw: string | null): string {
  return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/dashboard';
}

@Component({
  selector: 'app-login',
  imports: [FormsModule, RouterLink, SocialSignIn],
  templateUrl: './login.html',
})
export class Login {
  private auth = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  protected readonly email = signal('');
  protected readonly password = signal('');
  protected readonly submitting = signal(false);
  protected readonly error = signal<string | null>(null);
  // Kept separate from the form error so it can render as a banner at the top:
  // a failed provider round-trip lands back here with ?error=<code> before the
  // user has touched the email form at all.
  protected readonly socialError = signal<string | null>(
    socialErrorCopy(this.route.snapshot.queryParamMap.get('error')),
  );
  protected readonly redirectPath = safeRedirectPath(
    this.route.snapshot.queryParamMap.get('redirect'),
  );
  protected readonly justVerified = this.route.snapshot.queryParamMap.get('verified') === '1';
  protected readonly justReset = this.route.snapshot.queryParamMap.get('reset') === '1';

  protected readonly resending = signal(false);
  protected readonly resendSent = signal(false);
  protected readonly resendError = signal<string | null>(null);

  protected async resendVerification(): Promise<void> {
    if (this.resending() || this.resendSent()) return;
    this.resendError.set(null);
    const email = this.email().trim();
    if (!email) {
      this.resendError.set('Enter your email above, then try again.');
      return;
    }
    this.resending.set(true);
    const { error } = await this.auth.resendVerificationEmail(email);
    this.resending.set(false);
    if (error) {
      this.resendError.set(error);
      return;
    }
    this.resendSent.set(true);
  }

  protected async submit(): Promise<void> {
    if (this.submitting()) return;
    this.error.set(null);
    this.submitting.set(true);
    const { error } = await this.auth.signIn({
      email: this.email().trim(),
      password: this.password(),
    });
    this.submitting.set(false);
    if (error) {
      this.error.set(error);
      return;
    }
    void this.router.navigateByUrl(this.redirectPath);
  }
}
